/**
 * "May I show money?" for the current user in the current project.
 *
 * Financial numbers (store-category payout bands, salary/CTC, the redeployment
 * financial impact) are granted per USER in `financial_visibility_users`, not per
 * role — so unlike every other capability in the web app this cannot be read off
 * the auth store and has to be fetched.
 *
 * Starts as `false`, never `undefined`: a component rendering before the answer
 * arrives must show no numbers, not a flash of them.
 *
 * The answer is cached per project, but only for {@link ACCESS_TTL_MS} — the same
 * minute the server caches its own grant lookup for. An earlier version cached for the
 * LIFE OF THE TAB, and that was a real bug: a user who opened a page before being
 * granted access kept the "no" until they hard-reloaded, while the Dashboard's redeploy
 * modal (which reads `meta.financials_visible` off each API response instead of this
 * hook) started showing money immediately. Same user, same grant, two surfaces
 * disagreeing — purely because this cache never expired. A visibility flag that can be
 * changed server-side must be allowed to change client-side too.
 */
import { useEffect, useState } from 'react';
import { financialVisibilityAPI } from '../services/api';

/** Mirrors the server's own 60s grant cache, so a grant or revoke takes at most about
 *  a minute to show up on either side rather than needing a page reload. */
export const ACCESS_TTL_MS = 60 * 1000;

type CacheEntry = { allowed: boolean; expiresAt: number };

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<boolean>>();

const keyOf = (projectId?: number | null): string => String(projectId ?? 'all');

/** The cached answer if it is still fresh; null when absent or expired. */
const readCache = (key: string): boolean | null => {
  const hit = cache.get(key);
  if (!hit) return null;
  if (hit.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }
  return hit.allowed;
};

const fetchAccess = (projectId?: number | null): Promise<boolean> => {
  const key = keyOf(projectId);
  const cached = readCache(key);
  if (cached !== null) return Promise.resolve(cached);

  const existing = inFlight.get(key);
  if (existing) return existing;

  // Wrapped, not just `.catch`ed: this runs inside an effect, and a THROW here (a
  // stubbed api module in a test, a bundle that failed to load) would take the whole
  // page down over a display flag. Anything unexpected resolves to "no".
  const p = Promise.resolve()
    .then(() => financialVisibilityAPI.access(projectId ?? undefined))
    .then((res) => {
      const allowed = Boolean(res?.data?.data?.canViewFinancials);
      cache.set(key, { allowed, expiresAt: Date.now() + ACCESS_TTL_MS });
      return allowed;
    })
    // Fail closed. A network blip must not reveal a payout band, and the server
    // would redact the figures anyway — showing the panel would only produce
    // empty boxes.
    .catch(() => false)
    .finally(() => inFlight.delete(key));

  inFlight.set(key, p);
  return p;
};

export const useFinancialAccess = (projectId?: number | null): boolean => {
  const [canView, setCanView] = useState<boolean>(() => readCache(keyOf(projectId)) ?? false);

  useEffect(() => {
    let cancelled = false;
    fetchAccess(projectId).then((allowed) => {
      if (!cancelled) setCanView(allowed);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return canView;
};

/** Test seam / post-grant refresh — drops the per-tab cache. */
export const clearFinancialAccessCache = (): void => {
  cache.clear();
  inFlight.clear();
};
